import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Container,
    Grid,
    Typography,
    Button,
    Card,
    Stack,
    Chip,
    Avatar,
    Rating,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { motion, useInView } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ShieldIcon from "@mui/icons-material/Shield";
import PaidIcon from "@mui/icons-material/Paid";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HouseIcon from "@mui/icons-material/House";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BuildIcon from "@mui/icons-material/Build";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PublicIcon from "@mui/icons-material/Public";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { gottowhatsapp } from "../Utils/functions";

// ---------- Design tokens ----------
const colors = {
    navy: "#2B0710",
    blue: "#900F20",
    blueDark: "#5C0A16",
    teal: "#B23A4E",
    tealLight: "#E7B8C0",
    bg: "#FBF6F5",
    slate: "#6B7280",
};

const gradientPrimary = `linear-gradient(135deg, ${colors.blue} 0%, ${colors.teal} 100%)`;

// ---------- Shared motion variants ----------
const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" },
    }),
};

// ---------- Small reusable: animated counter ----------
const Counter = ({ to, suffix = "", duration = 1.6 }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (!inView) return;
        let start;
        const step = (timestamp) => {
            if (start === undefined) start = timestamp;
            const progress = Math.min((timestamp - start) / (duration * 1000), 1);
            setValue(Math.floor(progress * to));
            if (progress < 1) requestAnimationFrame(step);
            else setValue(to);
        };
        requestAnimationFrame(step);
    }, [inView, to, duration]);

    return (
        <Typography ref={ref} variant="h3" fontWeight={800} color="white">
            {value.toLocaleString()}
            {suffix}
        </Typography>
    );
};

// ---------- Decorative blurred background blobs ----------
const GradientBlob = ({ sx }) => (
    <Box
        aria-hidden="true"
        sx={{
            position: "absolute",
            borderRadius: "50%",
            filter: "blur(90px)",
            opacity: 0.35,
            zIndex: 0,
            ...sx,
        }}
    />
);

// ---------- Hero Section ----------
const HeroSection = () => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

    return (
        <Box
            component="section"
            sx={{
                position: "relative",
                overflow: "hidden",
                pt: { xs: 10, md: 14 },
                pb: { xs: 8, md: 12 },
                background: `linear-gradient(180deg, ${colors.bg} 0%, #FFFFFF 100%)`,
            }}
        >
            <GradientBlob sx={{ width: 420, height: 420, background: colors.blue, top: -160, left: -140 }} />
            <GradientBlob sx={{ width: 360, height: 360, background: colors.teal, bottom: -140, right: -120 }} />

            <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <motion.div initial="hidden" animate="show" variants={fadeUp}>
                            <Chip
                                label="Kenya's trusted moving partner"
                                sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    bgcolor: "rgba(144,15,32,0.08)",
                                    color: colors.blue,
                                }}
                            />
                        </motion.div>

                        <motion.div initial="hidden" animate="show" custom={1} variants={fadeUp}>
                            <Typography
                                variant="h2"
                                component="h1"
                                fontWeight={800}
                                letterSpacing="-0.02em"
                                sx={{ fontSize: { xs: "2.25rem", sm: "2.75rem", md: "3.25rem" }, lineHeight: 1.1, mb: 3 }}
                            >
                                Relocate with confidence.{" "}
                                <Box component="span" sx={{ background: gradientPrimary, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                                    Seamless moves, smiling faces.
                                </Box>
                            </Typography>
                        </motion.div>

                        <motion.div initial="hidden" animate="show" custom={2} variants={fadeUp}>
                            <Typography variant="h6" fontWeight={400} color={colors.slate} sx={{ mb: 4, maxWidth: 520 }}>
                                From homes to offices, Eazimove handles packing, transport and setup so your
                                next move feels effortless, on time and fully insured.
                            </Typography>
                        </motion.div>

                        <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => gottowhatsapp("Movers Enquiry")}
                                    sx={{
                                        background: gradientPrimary,
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 700,
                                        boxShadow: "0 10px 25px rgba(144,15,32,0.35)",
                                        transition: "transform 0.25s ease, box-shadow 0.25s ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 16px 32px rgba(144,15,32,0.45)",
                                            background: gradientPrimary,
                                        },
                                    }}
                                >
                                    Book Your Move
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<RequestQuoteIcon />}
                                    onClick={() => gottowhatsapp("Movers Enquiry")}
                                    sx={{
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 700,
                                        borderWidth: 2,
                                        borderColor: colors.blue,
                                        color: colors.blue,
                                        transition: "transform 0.25s ease, background 0.25s ease",
                                        "&:hover": {
                                            borderWidth: 2,
                                            transform: "translateY(-3px)",
                                            background: "rgba(144,15,32,0.06)",
                                        },
                                    }}
                                >
                                    Get a Quote
                                </Button>
                            </Stack>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.7, ease: "easeOut" }}
                        >
                            <Card
                                elevation={0}
                                sx={{
                                    position: "relative",
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    boxShadow: "0 30px 60px rgba(15,23,42,0.15)",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={require("./../../assets/common/movers1.png")}
                                    alt="Professional Eazimove movers carrying boxes"
                                    sx={{ width: "100%", display: "block", objectFit: "cover" }}
                                />
                            </Card>

                            {isMdUp && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6, duration: 0.6 }}
                                    style={{ position: "absolute", bottom: -24, left: -24 }}
                                >
                                    <Card
                                        sx={{
                                            px: 3,
                                            py: 2,
                                            borderRadius: 4,
                                            boxShadow: "0 20px 40px rgba(15,23,42,0.18)",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Avatar sx={{ bgcolor: colors.teal }}>
                                            <LocalShippingIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography fontWeight={800} lineHeight={1.1}>
                                                5,000+
                                            </Typography>
                                            <Typography variant="caption" color={colors.slate}>
                                                Successful moves
                                            </Typography>
                                        </Box>
                                    </Card>
                                </motion.div>
                            )}
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

// ---------- Trust badges ----------
const trustBadges = [
    { label: "Professional Movers", icon: <VerifiedUserIcon /> },
    { label: "Fully Insured", icon: <ShieldIcon /> },
    { label: "Affordable Rates", icon: <PaidIcon /> },
    { label: "On-Time Service", icon: <AccessTimeIcon /> },
];

const TrustBadges = () => (
    <Box component="section" sx={{ py: { xs: 5, md: 6 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
            <Grid container spacing={3} justifyContent="center">
                {trustBadges.map((badge, i) => (
                    <Grid item xs={6} sm={3} key={badge.label}>
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-60px" }}
                            custom={i}
                            variants={fadeUp}
                        >
                            <Stack alignItems="center" spacing={1.5} textAlign="center">
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "rgba(178,58,78,0.08)",
                                        color: colors.teal,
                                    }}
                                >
                                    {badge.icon}
                                </Box>
                                <Typography fontWeight={700} variant="body2">
                                    {badge.label}
                                </Typography>
                            </Stack>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
        </Container>
    </Box>
);

// ---------- Why choose Eazimove ----------
const services = [
    { title: "Home Moving", desc: "Careful, efficient household relocations of any size.", icon: <HouseIcon fontSize="large" /> },
    { title: "Office Relocation", desc: "Minimal downtime moves for growing businesses.", icon: <BusinessCenterIcon fontSize="large" /> },
    { title: "Packing Services", desc: "Expert packing with quality materials to protect your items.", icon: <Inventory2Icon fontSize="large" /> },
    { title: "Furniture Assembly", desc: "Disassembly and reassembly handled by trained crews.", icon: <BuildIcon fontSize="large" /> },
    { title: "Secure Transport", desc: "GPS-tracked vehicles and trained, vetted drivers.", icon: <LocalShippingIcon fontSize="large" /> },
    { title: "Nationwide Coverage", desc: "Reliable moves across the country, town to town.", icon: <PublicIcon fontSize="large" /> },
];

const WhyChooseSection = () => (
    <Box component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: colors.bg }}>
        <Container maxWidth="lg">
            <Box textAlign="center" sx={{ mb: { xs: 5, md: 7 } }}>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 1.5 }}>
                    Why Choose Eazimove
                </Typography>
                <Typography variant="body1" color={colors.slate} sx={{ maxWidth: 560, mx: "auto" }}>
                    Everything you need for a stress-free move, handled by one dependable team.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {services.map((service, i) => (
                    <Grid item xs={12} sm={6} md={4} key={service.title}>
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-60px" }}
                            custom={i}
                            variants={fadeUp}
                            style={{ height: "100%" }}
                        >
                            <Card
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    p: 3.5,
                                    borderRadius: 5,
                                    border: "1px solid rgba(15,23,42,0.06)",
                                    boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
                                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-6px)",
                                        boxShadow: "0 20px 40px rgba(15,23,42,0.12)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 3,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mb: 2,
                                        background: gradientPrimary,
                                        color: "white",
                                    }}
                                >
                                    {service.icon}
                                </Box>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                                    {service.title}
                                </Typography>
                                <Typography variant="body2" color={colors.slate}>
                                    {service.desc}
                                </Typography>
                            </Card>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
        </Container>
    </Box>
);

// ---------- Stats ----------
const StatsSection = () => (
    <Box
        component="section"
        sx={{ py: { xs: 7, md: 9 }, background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.blueDark} 100%)` }}
    >
        <Container maxWidth="lg">
            <Grid container spacing={4} justifyContent="center" textAlign="center">
                <Grid item xs={12} sm={4}>
                    <Counter to={5000} suffix="+" />
                    <Typography color="rgba(255,255,255,0.75)" sx={{ mt: 1 }}>
                        Successful Moves
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Counter to={98} suffix="%" />
                    <Typography color="rgba(255,255,255,0.75)" sx={{ mt: 1 }}>
                        Customer Satisfaction
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Typography variant="h3" fontWeight={800} color="white">
                        Same-Day
                    </Typography>
                    <Typography color="rgba(255,255,255,0.75)" sx={{ mt: 1 }}>
                        Booking Available
                    </Typography>
                </Grid>
            </Grid>
        </Container>
    </Box>
);

// ---------- Testimonials ----------
const testimonials = [
    { name: "Amina W.", role: "Home move, Nairobi", quote: "The crew was punctual, careful with every box, and made moving day feel easy.", rating: 5 },
    { name: "Brian K.", role: "Office relocation, Nakuru", quote: "We were back up and running the same day. Professional from start to finish.", rating: 5 },
    { name: "Grace N.", role: "Furniture assembly, Mombasa", quote: "Booked last minute and they still showed up on time and fully prepared.", rating: 4.5 },
];

const TestimonialsSection = () => (
    <Box component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
            <Box textAlign="center" sx={{ mb: { xs: 5, md: 7 } }}>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 1.5 }}>
                    What Our Customers Say
                </Typography>
                <Typography variant="body1" color={colors.slate}>
                    Real feedback from real moves.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {testimonials.map((t, i) => (
                    <Grid item xs={12} md={4} key={t.name}>
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-60px" }}
                            custom={i}
                            variants={fadeUp}
                            style={{ height: "100%" }}
                        >
                            <Card
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    p: 3.5,
                                    borderRadius: 5,
                                    border: "1px solid rgba(15,23,42,0.06)",
                                    boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
                                }}
                            >
                                <FormatQuoteIcon sx={{ color: colors.tealLight, fontSize: 36, mb: 1 }} />
                                <Typography variant="body1" sx={{ mb: 2.5, color: colors.navy }}>
                                    {t.quote}
                                </Typography>
                                <Rating value={t.rating} precision={0.5} readOnly sx={{ mb: 1.5 }} />
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: colors.blue }}>{t.name.charAt(0)}</Avatar>
                                    <Box>
                                        <Typography fontWeight={700} variant="body2">
                                            {t.name}
                                        </Typography>
                                        <Typography variant="caption" color={colors.slate}>
                                            {t.role}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Card>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
        </Container>
    </Box>
);

// ---------- Final CTA banner ----------
const FinalCtaSection = () => (
    <Box component="section" sx={{ py: { xs: 8, md: 10 }, px: 2 }}>
        <Container maxWidth="lg">
            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
            >
                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 6,
                        textAlign: "center",
                        py: { xs: 6, md: 8 },
                        px: { xs: 3, md: 6 },
                        background: gradientPrimary,
                        boxShadow: "0 30px 60px rgba(144,15,32,0.3)",
                    }}
                >
                    <GradientBlob sx={{ width: 260, height: 260, background: "white", top: -100, right: -80, opacity: 0.15 }} />
                    <Typography variant="h4" fontWeight={800} color="white" sx={{ mb: 1.5, position: "relative" }}>
                        Ready for a move that just works?
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.9)" sx={{ mb: 4, position: "relative" }}>
                        Get a free, no-obligation quote in minutes and lock in your moving date today.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => gottowhatsapp("Movers Enquiry")}
                        sx={{
                            position: "relative",
                            bgcolor: "white",
                            color: colors.blue,
                            fontWeight: 700,
                            px: 5,
                            py: 1.5,
                            borderRadius: 3,
                            transition: "transform 0.25s ease",
                            "&:hover": { bgcolor: "white", transform: "translateY(-3px)" },
                        }}
                    >
                        Book Your Move
                    </Button>
                </Box>
            </motion.div>
        </Container>
    </Box>
);

// ---------- Page ----------
const Eazimove = () => {
    return (
        <Box component="main" sx={{ bgcolor: colors.bg }}>
            <HeroSection />
            <TrustBadges />
            <WhyChooseSection />
            <StatsSection />
            <TestimonialsSection />
            <FinalCtaSection />
        </Box>
    );
};

export default Eazimove;